import {
	EditActions,
	type IInputProps,
	Input,
	type ISelect,
	Select,
} from '@/components';
import { MidTermDepartureEnum } from '@/constant';
import { useEditableDetail } from '@/hooks';
import {
	cn,
	composeOtherValue,
	deepCompare,
	getFieldInputProps,
	getFieldSelectProps,
	insertObjectIfV2,
	trimObjectValues,
} from '@/utils';
import React, { memo, useCallback, useMemo } from 'react';

import { DefaultOptionType } from 'antd/es/select';

import { FormikProps, useFormikContext } from 'formik';
import { Namespace } from 'i18next';
import { pick } from 'ramda';

export interface MidTermDepartureFieldProps<
	V extends Record<string, any> = Record<string, any>,
> {
	formik: any;
	options: DefaultOptionType[];
	name: string;
	otherName: string;
	containerClassName?: string;
	isDetail?: boolean;
	ns?: Namespace;
	getSelectAdditionalValues?: (
		value: string,
		prevValues: V
	) => Partial<V> | void;
	getInputAdditionalValues?: (
		value: string,
		prevValues: V
	) => Partial<V> | void;
	selectProps?: Partial<ISelect> & { regex?: RegExp; blockEnter?: boolean };
	inputProps?: Partial<IInputProps> & { regex?: RegExp; blockEnter?: boolean };
	handleOnSave?: (value: string, callback: () => void) => void;
}

const MidTermDepartureField: React.FC<MidTermDepartureFieldProps> = ({
	formik: formikProp,
	options,
	name = 'midTermDeparture',
	otherName = 'midTermDepartureOther',
	containerClassName = '',
	isDetail = false,
	ns = 'common',
	getSelectAdditionalValues,
	getInputAdditionalValues,
	selectProps,
	inputProps,
	handleOnSave = undefined,
}) => {
	const formikCtx = useFormikContext<Record<string, any>>();
	const formik = (formikProp as FormikProps<Record<string, any>>) || formikCtx;

	const { isUpdate, editable, handleLabelClick, setEditable } =
		useEditableDetail({ fieldName: name });

	const value: string = formik.values?.[name] ?? '';
	const otherValue: string = formik.values?.[otherName] ?? '';

	const isOther = value === MidTermDepartureEnum.OTHER;

	const labelText = useMemo(() => {
		if (isOther) {
			return composeOtherValue(otherValue, MidTermDepartureEnum.OTHER);
		}
		return value || '-';
	}, [isOther, value, otherValue]);

	const selectFieldProps = getFieldSelectProps(formik, name, {
		getAdditionalValues: getSelectAdditionalValues,
		blockSubmit: isDetail,
	});

	const inputFieldProps = getFieldInputProps(formik, otherName, {
		regex: inputProps?.regex,
		blockEnter: inputProps?.blockEnter,
		getAdditionalValues: getInputAdditionalValues,
	});

	const handleCancel = useCallback(() => {
		const initial = (formik.initialValues || {}) as Record<string, any>;
		formik.setValues((prev) => ({
			...prev,
			[name]: initial?.[name] ?? '',
			[otherName]: initial?.[otherName] ?? '',
		}));
		setEditable(false);
	}, [formik, name, otherName, setEditable]);

	const onChangeSelect = useCallback(
		(val: string) => {
			selectFieldProps.onChange(val);

			if (val !== MidTermDepartureEnum.OTHER) {
				if (handleOnSave) {
					handleOnSave?.(val, handleCancel);
					return;
				}

				formik.submitForm();
			}
		},
		[selectFieldProps, formik, handleOnSave, handleCancel]
	);

	const onSave = useCallback(async () => {
		if (
			deepCompare(
				trimObjectValues(pick([name, otherName], formik.values)),
				trimObjectValues(pick([name, otherName], formik.initialValues))
			)
		) {
			setEditable(false);
			return;
		}

		if (handleOnSave) {
			handleOnSave?.(value, handleCancel);
			return;
		}

		formik.submitForm();
	}, [formik, name, otherName, setEditable, handleOnSave, handleCancel, value]);

	return (
		<div className={cn('flex items-center gap-2 w-full', containerClassName)}>
			{isDetail && !editable ? (
				<div
					className={cn(
						'break-all box-border w-full text-[13px] xl:h-[30px] text-font2 py-[5px] xl:px-[11px] text-left overflow-hidden xl:hover:border xl:hover:border-gray1 cursor-text truncate whitespace-pre'
					)}
					onClick={handleLabelClick}
				>
					{labelText}
				</div>
			) : (
				<div className="flex w-full items-center gap-2">
					<Select
						isDetail={false}
						containerClassName="w-[200px] flex-initial"
						options={options}
						defaultValue=""
						{...selectFieldProps}
						{...insertObjectIfV2(isDetail, {
							onChange: onChangeSelect,
						})}
						{...selectProps}
					/>

					{isOther && (
						<Input
							isDetail={false}
							containerClassName="w-full"
							{...inputFieldProps}
							{...inputProps}
						/>
					)}

					{isUpdate && isDetail && isOther && (
						<EditActions onCancel={handleCancel} onSave={onSave} />
					)}
				</div>
			)}
		</div>
	);
};

export default memo(MidTermDepartureField);
